import React, { useState, useEffect } from 'react';
import { Mail, Lock, Eye, EyeOff, QrCode, Check } from 'lucide-react';
import { Button } from '../Button';
import { Input } from '../Input';
import { useAuth } from '../../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema, registerSchema, type LoginFormData, type RegisterFormData } from '../../utils/validation';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../../utils/constants';
import { GoogleAuthButton } from './GoogleAuthButton';
import { analytics } from '../../services/analytics';

export function AuthPage() {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);

  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate(ROUTES.DASHBOARD);
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Set initial state based on current route
  useEffect(() => {
    if (location.pathname === ROUTES.REGISTER) {
      setIsLogin(false);
    } else {
      setIsLogin(true);
    }
  }, [location.pathname]);
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const { login, register: registerUser } = useAuth();

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const registerForm = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onLoginSubmit = async (data: LoginFormData) => {
    try {
      await login(data);
      navigate(ROUTES.DASHBOARD);
    } catch (error) {
      // Error is handled by AuthContext
    }
  };

  const onRegisterSubmit = async (data: RegisterFormData) => {
    analytics.capture('signup_started');
    try {
      await registerUser(data);
      analytics.capture('signup_completed');
      navigate(ROUTES.DASHBOARD);
    } catch (error) {
      // Error is handled by AuthContext
    }
  };

  const handleTabSwitch = (loginMode: boolean) => {
    setIsLogin(loginMode);
    navigate(loginMode ? ROUTES.LOGIN : ROUTES.REGISTER);
  };

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[--clinqr-bg-subtle]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[--clinqr-primary] mx-auto mb-4"></div>
          <p className="text-[--clinqr-text-secondary]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      {/* Left Section - Form */}
      <div className="w-[60%] bg-white flex flex-col">
        {/* Logo */}
        <div className="p-10">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-8 h-8 bg-[--clinqr-primary] rounded-lg flex items-center justify-center">
              <QrCode className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <span className="text-xl font-semibold text-[--clinqr-text-primary]">Clinqr</span>
          </div>
        </div>

        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center px-10">
          <div className="w-full max-w-md">
            {/* Tab Switcher */}
            <div className="flex border-b-2 border-[--clinqr-border] mb-12">
              <button
                onClick={() => handleTabSwitch(true)}
                className={`flex-1 pb-4 text-center transition-colors relative ${
                  isLogin ? 'text-[--clinqr-text-primary]' : 'text-[--clinqr-text-secondary]'
                }`}
              >
                <span className="text-lg font-medium">Sign In</span>
                {isLogin && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[--clinqr-primary]"></div>
                )}
              </button>
              <button
                onClick={() => handleTabSwitch(false)}
                className={`flex-1 pb-4 text-center transition-colors relative ${
                  !isLogin ? 'text-[--clinqr-text-primary]' : 'text-[--clinqr-text-secondary]'
                }`}
              >
                <span className="text-lg font-medium">Create Account</span>
                {!isLogin && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[--clinqr-primary]"></div>
                )}
              </button>
            </div>

            {/* Login Form */}
            {isLogin ? (
              <div>
                <h2 className="text-[32px] font-semibold text-[--clinqr-text-primary] mb-2">
                  Welcome back
                </h2>
                <p className="text-[--clinqr-text-secondary] mb-6">
                  Sign in to your account
                </p>

                <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-6">
                  {/* Email Input */}
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[--clinqr-text-muted]" strokeWidth={2} />
                    <Input
                      type="email"
                      placeholder="Email address"
                      className="h-12 pl-12 !bg-white !border-[--clinqr-border] !text-[--clinqr-text-primary] !placeholder:text-[--clinqr-text-muted]"
                      {...loginForm.register('email')}
                    />
                  </div>

                  {/* Password Input */}
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[--clinqr-text-muted]" strokeWidth={2} />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Password"
                      className="h-12 pl-12 pr-12 !bg-white !border-[--clinqr-border] !text-[--clinqr-text-primary] !placeholder:text-[--clinqr-text-muted]"
                      {...loginForm.register('password')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[--clinqr-text-muted] hover:text-[--clinqr-text-primary]"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" strokeWidth={2} />
                      ) : (
                        <Eye className="w-5 h-5" strokeWidth={2} />
                      )}
                    </button>
                  </div>

                  {/* Forgot Password */}
                  <div className="text-right">
                    <a href="#" className="text-sm text-[--clinqr-primary] hover:underline">
                      Forgot password?
                    </a>
                  </div>

                  {/* Sign In Button */}
                  <Button
                    type="submit"
                    variant="primary"
                    className="w-full h-12"
                    disabled={loginForm.formState.isSubmitting}
                  >
                    {loginForm.formState.isSubmitting ? 'Signing In...' : 'Sign In'}
                  </Button>

                  {/* Divider */}
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-[--clinqr-border]"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-white text-[--clinqr-text-muted]">OR</span>
                    </div>
                  </div>

                  {/* Google OAuth */}
                  <GoogleAuthButton />

                  {/* Footer */}
                  <p className="text-center text-[--clinqr-text-secondary] mt-8">
                    Don't have an account?{' '}
                    <button
                      type="button"
                      onClick={() => handleTabSwitch(false)}
                      className="text-[--clinqr-primary] hover:underline font-medium"
                    >
                      Sign up
                    </button>
                  </p>
                </form>
              </div>
            ) : (
              /* Register Form */
              <div>
                <h2 className="text-[32px] font-semibold text-[--clinqr-text-primary] mb-2">
                  Create your account
                </h2>
                <p className="text-[--clinqr-text-secondary] mb-6">
                  Start your free trial, no credit card required
                </p>

                <form onSubmit={registerForm.handleSubmit(onRegisterSubmit)} className="space-y-6">
                  {/* Email Input */}
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[--clinqr-text-muted]" strokeWidth={2} />
                    <Input
                      type="email"
                      placeholder="Email address"
                      className="h-12 pl-12 !bg-white !border-[--clinqr-border] !text-[--clinqr-text-primary] !placeholder:text-[--clinqr-text-muted]"
                      {...registerForm.register('email')}
                    />
                  </div>

                  {/* Password Input */}
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[--clinqr-text-muted]" strokeWidth={2} />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Password"
                      className="h-12 pl-12 pr-12 !bg-white !border-[--clinqr-border] !text-[--clinqr-text-primary] !placeholder:text-[--clinqr-text-muted]"
                      {...registerForm.register('password')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[--clinqr-text-muted] hover:text-[--clinqr-text-primary]"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" strokeWidth={2} />
                      ) : (
                        <Eye className="w-5 h-5" strokeWidth={2} />
                      )}
                    </button>
                  </div>

                  {/* Confirm Password Input */}
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[--clinqr-text-muted]" strokeWidth={2} />
                    <Input
                      type="password"
                      placeholder="Confirm password"
                      className="h-12 pl-12 !bg-white !border-[--clinqr-border] !text-[--clinqr-text-primary] !placeholder:text-[--clinqr-text-muted]"
                      {...registerForm.register('confirmPassword')}
                    />
                  </div>

                  {/* Terms Checkbox */}
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-[--clinqr-primary]"
                    />
                    <span className="text-sm text-[--clinqr-text-secondary]">
                      I agree to the{' '}
                      <a href="#" className="text-[--clinqr-primary] underline">
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a href="#" className="text-[--clinqr-primary] underline">
                        Privacy Policy
                      </a>
                    </span>
                  </label>

                  {/* Create Account Button */}
                  <Button
                    type="submit"
                    variant="primary"
                    className="w-full h-12"
                    disabled={!agreedToTerms || registerForm.formState.isSubmitting}
                  >
                    {registerForm.formState.isSubmitting ? 'Creating Account...' : 'Create Account'}
                  </Button>

                  {/* Divider */}
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-[--clinqr-border]"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-white text-[--clinqr-text-muted]">OR</span>
                    </div>
                  </div>

                  {/* Google OAuth */}
                  <GoogleAuthButton />

                  {/* Footer */}
                  <p className="text-center text-[--clinqr-text-secondary] mt-8">
                    Already have an account?{' '}
                    <button
                      type="button"
                      onClick={() => handleTabSwitch(true)}
                      className="text-[--clinqr-primary] hover:underline font-medium"
                    >
                      Sign in
                    </button>
                  </p>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Section - Brand Visual */}
      <div className="w-[40%] bg-[--clinqr-primary] relative overflow-hidden flex items-center justify-center p-12">
        {/* Geometric shapes instead of blur */}
        <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-white/5 rounded-[60px] -rotate-12 translate-x-1/3 -translate-y-1/3"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-white/5 rounded-[60px] rotate-12 -translate-x-1/3 translate-y-1/3"></div>

        {/* Dot pattern */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 1px)`,
            backgroundSize: '32px 32px',
          }}
        />

        <div className="relative z-10 text-center text-white">
          {/* Large QR Code Illustration */}
          <div className="w-64 h-64 bg-white/20 rounded-2xl p-6 mx-auto mb-8 relative">
            <div className="w-full h-full bg-white/30 rounded-lg grid grid-cols-8 gap-1 p-2">
              {Array.from({ length: 64 }).map((_, i) => (
                <div
                  key={i}
                  className={`${Math.random() > 0.5 ? 'bg-white' : 'bg-transparent'} rounded-sm`}
                />
              ))}
            </div>

            {/* Floating Analytics Cards */}
            <div className="absolute -top-4 -right-4 bg-white/90 rounded-lg shadow-lg p-3 text-left">
              <p className="text-xs text-[--clinqr-text-muted]">Scans</p>
              <p className="text-lg font-semibold text-[--clinqr-text-primary]">12.4K</p>
            </div>

            <div className="absolute -bottom-4 -left-4 bg-white/90 rounded-lg shadow-lg p-3 text-left">
              <p className="text-xs text-[--clinqr-text-muted]">Devices</p>
              <p className="text-lg font-semibold text-[--clinqr-text-primary]">Mobile</p>
            </div>
          </div>

          <h3 className="text-[32px] font-semibold mb-6">
            Smart QR codes for modern marketing
          </h3>

          <ul className="space-y-3 text-lg">
            <li className="flex items-center justify-center gap-2">
              <Check className="w-5 h-5" strokeWidth={2} />
              Dynamic routing
            </li>
            <li className="flex items-center justify-center gap-2">
              <Check className="w-5 h-5" strokeWidth={2} />
              Real-time analytics
            </li>
            <li className="flex items-center justify-center gap-2">
              <Check className="w-5 h-5" strokeWidth={2} />
              A/B testing
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
